/**
 * Centralized SEO copy for each main public page.
 *
 * Each entry maps a page key to localized title + description.
 * Imported by the corresponding Page component:
 *
 *   import { PAGE_SEO } from '../config/seoCopy';
 *   <SEO title={PAGE_SEO.restaurant.title} description={PAGE_SEO.restaurant.description} />
 *
 * Keeping all copy in one file makes it trivial to review,
 * translate and tweak from a single place without touching components.
 */

export const PAGE_SEO = {
  home: {
    title: {
      ca: "UGOLF ARAVELL Andorra - Camp de Golf als Pirineus",
      es: "UGOLF ARAVELL Andorra - Campo de Golf en los Pirineos",
      fr: "UGOLF ARAVELL Andorre - Parcours de Golf dans les Pyrénées",
      en: "UGOLF ARAVELL Andorra - Golf Course in the Pyrenees",
    },
    description: {
      ca: "UGOLF ARAVELL Andorra - Camp de golf de 18 forats dissenyat per José María Olazábal als Pirineus catalans. Reserva el teu green fee, descobreix el restaurant i les nostres instal·lacions.",
      es: "UGOLF ARAVELL Andorra - Campo de golf de 18 hoyos diseñado por José María Olazábal en los Pirineos catalanes. Reserva tu green fee, descubre el restaurante y nuestras instalaciones.",
      fr: "UGOLF ARAVELL Andorre - Parcours de golf 18 trous dessiné par José María Olazábal dans les Pyrénées catalanes. Réservez votre green fee, découvrez le restaurant et nos installations.",
      en: "UGOLF ARAVELL Andorra - 18-hole golf course designed by José María Olazábal in the Catalan Pyrenees. Book your green fee and discover our restaurant and facilities.",
    },
  },

  elCamp: {
    title: {
      ca: "El Camp - UGOLF ARAVELL Andorra",
      es: "El Campo - UGOLF ARAVELL Andorra",
      fr: "Le Parcours - UGOLF ARAVELL Andorre",
      en: "The Course - UGOLF ARAVELL Andorra",
    },
    description: {
      ca: "Descobreix el camp de golf de 18 forats dissenyat per José María Olazábal als Pirineus. Recorregut, dificultat, scorecard i fotografies del recorregut.",
      es: "Descubre el campo de golf de 18 hoyos diseñado por José María Olazábal en los Pirineos. Recorrido, dificultad, scorecard y fotografías del trazado.",
      fr: "Découvrez le parcours de golf 18 trous dessiné par José María Olazábal dans les Pyrénées. Tracé, difficulté, scorecard et photographies.",
      en: "Discover our 18-hole golf course designed by José María Olazábal in the Pyrenees. Layout, difficulty, scorecard and photos of every hole.",
    },
  },

  jugar: {
    title: {
      ca: "Jugar a UGOLF Aravell - Green Fees i Tarifes",
      es: "Jugar en UGOLF Aravell - Green Fees y Tarifas",
      fr: "Jouer à UGOLF Aravell - Green Fees et Tarifs",
      en: "Play at UGOLF Aravell - Green Fees and Rates",
    },
    description: {
      ca: "Tarifes de green fee, abonaments i condicions per jugar al camp d'UGOLF ARAVELL Andorra. Consulta els preus de temporada alta i baixa.",
      es: "Tarifas de green fee, abonos y condiciones para jugar en el campo de UGOLF ARAVELL Andorra. Consulta los precios de temporada alta y baja.",
      fr: "Tarifs de green fee, abonnements et conditions pour jouer sur le parcours d'UGOLF ARAVELL Andorre. Consultez les prix de haute et basse saison.",
      en: "Green fee rates, memberships and conditions to play at UGOLF ARAVELL Andorra. See high and low season prices.",
    },
  },

  competicio: {
    title: {
      ca: "Competicions i Tornejos - UGOLF ARAVELL Andorra",
      es: "Competiciones y Torneos - UGOLF ARAVELL Andorra",
      fr: "Compétitions et Tournois - UGOLF ARAVELL Andorre",
      en: "Competitions and Tournaments - UGOLF ARAVELL Andorra",
    },
    description: {
      ca: "Calendari de competicions, tornejos i campionats d'UGOLF ARAVELL Andorra. Apunta't i viu el golf de competició al millor camp dels Pirineus.",
      es: "Calendario de competiciones, torneos y campeonatos de UGOLF ARAVELL Andorra. Apúntate y vive el golf de competición en el mejor campo de los Pirineos.",
      fr: "Calendrier de compétitions, tournois et championnats d'UGOLF ARAVELL Andorre. Inscrivez-vous et vivez le golf de compétition.",
      en: "Calendar of competitions, tournaments and championships at UGOLF ARAVELL Andorra. Sign up and experience golf at its best in the Pyrenees.",
    },
  },

  iniciacio: {
    title: {
      ca: "Acadèmia de Golf - Classes i Iniciació",
      es: "Academia de Golf - Clases e Iniciación",
      fr: "Académie de Golf - Cours et Initiation",
      en: "Golf Academy - Lessons and Beginners",
    },
    description: {
      ca: "Acadèmia de golf d'UGOLF ARAVELL Andorra. Classes individuals i de grup, cursos d'iniciació i programes per a totes les edats.",
      es: "Academia de golf de UGOLF ARAVELL Andorra. Clases individuales y de grupo, cursos de iniciación y programas para todas las edades.",
      fr: "Académie de golf d'UGOLF ARAVELL Andorre. Cours particuliers et collectifs, initiation et programmes pour tous les âges.",
      en: "Golf academy at UGOLF ARAVELL Andorra. Private and group lessons, beginner courses and programs for all ages.",
    },
  },

  drivingRange: {
    title: {
      ca: "Driving Range - Camp de Pràctiques d'UGOLF ARAVELL",
      es: "Driving Range - Campo de Prácticas de UGOLF ARAVELL",
      fr: "Driving Range - Practice d'UGOLF ARAVELL",
      en: "Driving Range - Practice Area at UGOLF ARAVELL",
    },
    description: {
      ca: "Camp de pràctiques cobert i descobert amb vistes als Pirineus. Putting green, chipping area i bunker per millorar el teu joc.",
      es: "Campo de prácticas cubierto y descubierto con vistas a los Pirineos. Putting green, chipping area y bunker para mejorar tu juego.",
      fr: "Practice couvert et découvert avec vue sur les Pyrénées. Putting green, chipping area et bunker pour améliorer votre jeu.",
      en: "Indoor and outdoor practice area with Pyrenees views. Putting green, chipping area and bunker to refine every part of your game.",
    },
  },

  restaurant: {
    title: {
      ca: "Restaurant - UGOLF ARAVELL Andorra",
      es: "Restaurante - UGOLF ARAVELL Andorra",
      fr: "Restaurant - UGOLF ARAVELL Andorre",
      en: "Restaurant - UGOLF ARAVELL Andorra",
    },
    description: {
      ca: "Gastronomia de proximitat amb vistes als Pirineus al restaurant d'UGOLF ARAVELL Andorra. Cuina mediterrània, terrassa i reserves.",
      es: "Gastronomía de proximidad con vistas a los Pirineos en el restaurante de UGOLF ARAVELL Andorra. Cocina mediterránea, terraza y reservas.",
      fr: "Gastronomie de proximité avec vue sur les Pyrénées au restaurant d'UGOLF ARAVELL Andorre. Cuisine méditerranéenne, terrasse et réservations.",
      en: "Local cuisine with Pyrenees views at the UGOLF ARAVELL Andorra restaurant. Mediterranean menu, terrace and reservations.",
    },
  },

  allotjament: {
    title: {
      ca: "Allotjament - UGOLF ARAVELL Andorra",
      es: "Alojamiento - UGOLF ARAVELL Andorra",
      fr: "Hébergement - UGOLF ARAVELL Andorre",
      en: "Accommodation - UGOLF ARAVELL Andorra",
    },
    description: {
      ca: "Opcions d'allotjament a la zona d'UGOLF ARAVELL Andorra. Hotels, apartaments i paquets golf + estada per gaudir d'una escapada perfecta.",
      es: "Opciones de alojamiento en la zona de UGOLF ARAVELL Andorra. Hoteles, apartamentos y paquetes golf + estancia para una escapada perfecta.",
      fr: "Options d'hébergement près d'UGOLF ARAVELL Andorre. Hôtels, appartements et forfaits golf + séjour pour une escapade parfaite.",
      en: "Accommodation options near UGOLF ARAVELL Andorra. Hotels, apartments and golf + stay packages for a perfect getaway.",
    },
  },

  correspondencies: {
    title: {
      ca: "Correspondències - UGOLF ARAVELL Andorra",
      es: "Correspondencias - UGOLF ARAVELL Andorra",
      fr: "Correspondances - UGOLF ARAVELL Andorre",
      en: "Reciprocal Clubs - UGOLF ARAVELL Andorra",
    },
    description: {
      ca: "Acords de correspondència amb altres camps de golf. Descobreix les condicions especials per als nostres abonats.",
      es: "Acuerdos de correspondencia con otros campos de golf. Descubre las condiciones especiales para nuestros abonados.",
      fr: "Accords de correspondance avec d'autres parcours de golf. Découvrez les conditions spéciales pour nos membres.",
      en: "Reciprocal agreements with other golf clubs. Discover the special conditions for our members.",
    },
  },

  contacte: {
    title: {
      ca: "Contacte - UGOLF ARAVELL Andorra",
      es: "Contacto - UGOLF ARAVELL Andorra",
      fr: "Contact - UGOLF ARAVELL Andorre",
      en: "Contact - UGOLF ARAVELL Andorra",
    },
    description: {
      ca: "Informació de contacte d'UGOLF ARAVELL Andorra: telèfon, email, horaris i com arribar al camp situat als Pirineus catalans.",
      es: "Información de contacto de UGOLF ARAVELL Andorra: teléfono, email, horarios y cómo llegar al campo situado en los Pirineos catalanes.",
      fr: "Coordonnées d'UGOLF ARAVELL Andorre : téléphone, e-mail, horaires et accès au parcours situé dans les Pyrénées catalanes.",
      en: "Contact information for UGOLF ARAVELL Andorra: phone, email, hours and how to reach the course in the Catalan Pyrenees.",
    },
  },

  equip: {
    title: {
      ca: "L'Equip - UGOLF ARAVELL Andorra",
      es: "El Equipo - UGOLF ARAVELL Andorra",
      fr: "L'Équipe - UGOLF ARAVELL Andorre",
      en: "The Team - UGOLF ARAVELL Andorra",
    },
    description: {
      ca: "Coneix l'equip humà que fa possible UGOLF ARAVELL Andorra: direcció, professionals de golf, manteniment i atenció al client.",
      es: "Conoce al equipo humano que hace posible UGOLF ARAVELL Andorra: dirección, profesionales de golf, mantenimiento y atención al cliente.",
      fr: "Découvrez l'équipe qui fait vivre UGOLF ARAVELL Andorre : direction, professionnels du golf, maintenance et service client.",
      en: "Meet the team behind UGOLF ARAVELL Andorra: management, golf professionals, course maintenance and customer service.",
    },
  },

  membres: {
    title: {
      ca: "Àrea de Membres - UGOLF ARAVELL Andorra",
      es: "Área de Miembros - UGOLF ARAVELL Andorra",
      fr: "Espace Membres - UGOLF ARAVELL Andorre",
      en: "Members Area - UGOLF ARAVELL Andorra",
    },
    description: {
      ca: "Àrea privada per a abonats d'UGOLF ARAVELL Andorra. Reserves, notícies i avantatges exclusius per als nostres membres.",
      es: "Área privada para abonados de UGOLF ARAVELL Andorra. Reservas, noticias y ventajas exclusivas para nuestros miembros.",
      fr: "Espace privé pour les membres d'UGOLF ARAVELL Andorre. Réservations, actualités et avantages exclusifs.",
      en: "Private area for UGOLF ARAVELL Andorra members. Reservations, news and exclusive benefits.",
    },
  },

  fesTeAbonat: {
    title: {
      ca: "Fes-te Abonat - UGOLF ARAVELL Andorra",
      es: "Hazte Abonado - UGOLF ARAVELL Andorra",
      fr: "Devenez Membre - UGOLF ARAVELL Andorre",
      en: "Become a Member - UGOLF ARAVELL Andorra",
    },
    description: {
      ca: "Tipus d'abonament i quotes per ser membre d'UGOLF ARAVELL Andorra. Avantatges, condicions i com fer-te abonat al camp.",
      es: "Tipos de abono y cuotas para ser miembro de UGOLF ARAVELL Andorra. Ventajas, condiciones y cómo hacerte abonado.",
      fr: "Formules d'abonnement pour devenir membre d'UGOLF ARAVELL Andorre. Avantages, conditions et inscription.",
      en: "Membership types and fees at UGOLF ARAVELL Andorra. Benefits, conditions and how to join the club.",
    },
  },

  programaNuts: {
    title: {
      ca: "Programa NUTS - Iniciació al Golf - UGOLF ARAVELL",
      es: "Programa NUTS - Iniciación al Golf - UGOLF ARAVELL",
      fr: "Programme NUTS - Initiation au Golf - UGOLF ARAVELL",
      en: "NUTS Program - Golf Initiation - UGOLF ARAVELL",
    },
    description: {
      ca: "Descobreix el golf amb el programa NUTS d'UGOLF ARAVELL Andorra. Sessions d'iniciació al golf per només 60€. Reserva la teva ara.",
      es: "Descubre el golf con el programa NUTS de UGOLF ARAVELL Andorra. Sesiones de iniciación al golf por solo 60€. Reserva la tuya ahora.",
      fr: "Découvrez le golf avec le programme NUTS d'UGOLF ARAVELL Andorre. Séances d'initiation au golf pour seulement 60€. Réservez la vôtre maintenant.",
      en: "Discover golf with the NUTS program at UGOLF ARAVELL Andorra. Golf initiation sessions for only 60€. Book yours now.",
    },
  },

  premsa: {
    title: {
      ca: "Premsa i Notícies - UGOLF ARAVELL Andorra",
      es: "Prensa y Noticias - UGOLF ARAVELL Andorra",
      fr: "Presse et Actualités - UGOLF ARAVELL Andorre",
      en: "Press and News - UGOLF ARAVELL Andorra",
    },
    description: {
      ca: "Notícies, comunicats de premsa i aparicions als mitjans d'UGOLF ARAVELL Andorra.",
      es: "Noticias, comunicados de prensa y apariciones en medios de UGOLF ARAVELL Andorra.",
      fr: "Actualités, communiqués de presse et apparitions médiatiques d'UGOLF ARAVELL Andorre.",
      en: "News, press releases and media appearances about UGOLF ARAVELL Andorra.",
    },
  },

  careers: {
    title: {
      ca: "Treballa amb Nosaltres - UGOLF ARAVELL Andorra",
      es: "Trabaja con Nosotros - UGOLF ARAVELL Andorra",
      fr: "Carrières - UGOLF ARAVELL Andorre",
      en: "Careers - UGOLF ARAVELL Andorra",
    },
    description: {
      ca: "Ofertes de feina actuals a UGOLF ARAVELL Andorra. Uneix-te a l'equip que fa possible el millor camp de golf dels Pirineus.",
      es: "Ofertas de trabajo actuales en UGOLF ARAVELL Andorra. Únete al equipo que hace posible el mejor campo de golf de los Pirineos.",
      fr: "Offres d'emploi actuelles à UGOLF ARAVELL Andorre. Rejoignez l'équipe du meilleur parcours de golf des Pyrénées.",
      en: "Current job openings at UGOLF ARAVELL Andorra. Join the team behind the best golf course in the Pyrenees.",
    },
  },
};
